import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PageBackground from '../components/PageBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [settings, setSettings] = useState({
    login_page_title: 'MSF RACING',
    login_page_subtitle: 'Fantasy League',
    login_page_welcome_text: 'Welcome Back',
    login_page_register_text: 'Create Account'
  });

  useEffect(() => {
    fetchSettings();
    // Clear session flag so rules popup shows on next login
    sessionStorage.removeItem('msf_rules_shown');
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/system/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleGoogleLogin = () => {
    sessionStorage.removeItem('msf_rules_shown');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await axios.post(`${BACKEND_URL}${endpoint}`, formData, {
        withCredentials: true,
      });
      const data = response.data || {};
      // Persist session_token as a fallback for browsers that block third-party
      // cookies on custom production domains.
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      const user = { ...data };
      delete user.session_token;
      toast.success(isLogin ? 'Logged in successfully' : 'Registered successfully');
      sessionStorage.removeItem('msf_rules_shown');
      navigate('/dashboard', { state: { user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  return (
    <PageBackground pageKey="login">
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading font-black text-5xl mb-2">
            <span className="text-primary">{settings.login_page_title?.split(' ')[0] || 'MSF'}</span> {settings.login_page_title?.split(' ').slice(1).join(' ') || 'RACING'}
          </h1>
          <p className="text-zinc-400">{settings.login_page_subtitle || 'Fantasy League'}</p>
        </div>

        <div className="bg-card border border-zinc-800 p-8">
          <h2 className="font-heading font-bold text-2xl mb-6">
            {isLogin ? (settings.login_page_welcome_text || 'Welcome Back') : (settings.login_page_register_text || 'Create Account')}
          </h2>

          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            className="w-full bg-white text-black py-3 px-4 font-semibold hover:bg-zinc-200 transition-colors mb-6 flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-zinc-400">Or</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  data-testid="register-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                  required={!isLogin}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Email
              </label>
              <input
                type="email"
                data-testid="login-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Password
              </label>
              <input
                type="password"
                data-testid="login-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="auth-submit-button"
              className="w-full btn-skew bg-primary text-white py-3 font-heading font-bold uppercase tracking-wider neon-glow-hover"
            >
              <span>{isLogin ? 'Login' : 'Register'}</span>
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode-button"
              className="text-sm text-zinc-400 hover:text-primary transition-colors"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </PageBackground>
  );
}
